<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('passwordGen.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('passwordGen.title') }}</h1>
      <p>{{ $t('passwordGen.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="password-display">
        <code class="password-text">{{ password || $t('passwordGen.placeholder') }}</code>
        <button class="btn-copy" @click="copyPassword" :title="$t('common.copy')">📋</button>
        <button class="btn-refresh" @click="generate" title="regenerate">🔄</button>
      </div>

      <div class="password-strength" v-if="password">
        <div class="strength-bar">
          <div class="strength-fill" :style="{ width: strengthPercent + '%', background: strengthColor }"></div>
        </div>
        <span class="strength-label" :style="{ color: strengthColor }">{{ strengthLabel }}</span>
      </div>

      <div class="settings">
        <div class="setting-row">
          <label>{{ $t('passwordGen.length') }}</label>
          <div class="range-control">
            <input type="range" v-model.number="length" min="4" max="128" @input="generate" />
            <input type="number" v-model.number="length" min="4" max="128" class="range-number" @change="generate" />
          </div>
        </div>

        <div class="setting-row">
          <label class="checkbox-label">
            <input type="checkbox" v-model="useUppercase" @change="generate" />
            {{ $t('passwordGen.uppercase') }} (A-Z)
          </label>
        </div>
        <div class="setting-row">
          <label class="checkbox-label">
            <input type="checkbox" v-model="useLowercase" @change="generate" />
            {{ $t('passwordGen.lowercase') }} (a-z)
          </label>
        </div>
        <div class="setting-row">
          <label class="checkbox-label">
            <input type="checkbox" v-model="useNumbers" @change="generate" />
            {{ $t('passwordGen.numbers') }} (0-9)
          </label>
        </div>
        <div class="setting-row">
          <label class="checkbox-label">
            <input type="checkbox" v-model="useSymbols" @change="generate" />
            {{ $t('passwordGen.symbols') }} (!@#$%^&*...)
          </label>
        </div>
        <div class="setting-row">
          <label class="checkbox-label">
            <input type="checkbox" v-model="excludeAmbiguous" @change="generate" />
            {{ $t('passwordGen.excludeAmbiguous') }}
          </label>
        </div>
      </div>

      <div class="batch-section">
        <h3>{{ $t('passwordGen.batchTitle') }}</h3>
        <div class="batch-controls">
          <label>{{ $t('passwordGen.batchCount') }}</label>
          <input type="number" v-model.number="batchCount" min="1" max="100" class="batch-input" />
          <button class="btn btn-primary" @click="generateBatch">{{ $t('passwordGen.batchGenerate') }}</button>
        </div>
        <textarea v-if="batchPasswords" v-model="batchPasswords" rows="6" readonly class="batch-output"></textarea>
      </div>

      <div class="action-bar" v-if="statusMsg">
        <span class="status-msg">{{ statusMsg }}</span>
      </div>
    </div>

    <div class="tool-card info-card">
      <h2>{{ $t('passwordGen.whatIs') }}</h2>
      <p>{{ $t('passwordGen.whatIsDesc') }}</p>
      <h3>{{ $t('passwordGen.tipsTitle') }}</h3>
      <ul>
        <li v-for="(item, i) in $t('passwordGen.tips')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PasswordGenerator',
  data() {
    return {
      password: '',
      length: 16,
      useUppercase: true,
      useLowercase: true,
      useNumbers: true,
      useSymbols: true,
      excludeAmbiguous: false,
      batchCount: 10,
      batchPasswords: '',
      statusMsg: ''
    }
  },
  computed: {
    charset() {
      let chars = ''
      const ambiguous = 'ilLI1oO0'
      if (this.useUppercase) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      if (this.useLowercase) chars += 'abcdefghijklmnopqrstuvwxyz'
      if (this.useNumbers) chars += '0123456789'
      if (this.useSymbols) chars += '!@#$%^&*()_+-=[]{}|;:,.<>?'
      if (this.excludeAmbiguous) {
        chars = chars.split('').filter(c => !ambiguous.includes(c)).join('')
      }
      return chars
    },
    strengthPercent() {
      if (!this.password) return 0
      const len = this.password.length
      let score = 0
      if (len >= 8) score += 25
      if (len >= 12) score += 15
      if (len >= 16) score += 10
      if (/[a-z]/.test(this.password)) score += 10
      if (/[A-Z]/.test(this.password)) score += 10
      if (/[0-9]/.test(this.password)) score += 10
      if (/[^a-zA-Z0-9]/.test(this.password)) score += 20
      return Math.min(100, score)
    },
    strengthColor() {
      const p = this.strengthPercent
      if (p < 40) return '#ef4444'
      if (p < 70) return '#f59e0b'
      return '#10b981'
    },
    strengthLabel() {
      const p = this.strengthPercent
      if (p < 40) return this.$t('passwordGen.weak')
      if (p < 70) return this.$t('passwordGen.medium')
      return this.$t('passwordGen.strong')
    }
  },
  mounted() { this.generate() },
  methods: {
    generate() {
      if (!this.charset) {
        this.password = ''
        return
      }
      const arr = new Uint32Array(this.length)
      crypto.getRandomValues(arr)
      this.password = Array.from(arr).map(n => this.charset[n % this.charset.length]).join('')
    },
    generateBatch() {
      if (!this.charset) return
      const passwords = []
      for (let i = 0; i < this.batchCount; i++) {
        const arr = new Uint32Array(this.length)
        crypto.getRandomValues(arr)
        passwords.push(Array.from(arr).map(n => this.charset[n % this.charset.length]).join(''))
      }
      this.batchPasswords = passwords.join('\n')
    },
    async copyPassword() {
      if (!this.password) return
      try {
        await navigator.clipboard.writeText(this.password)
        this.statusMsg = this.$t('common.copied')
        setTimeout(() => { this.statusMsg = '' }, 2000)
      } catch { this.statusMsg = this.$t('common.copyFailed') }
    }
  }
}
</script>

<style scoped>
.tool-page { max-width: 800px; margin: 0 auto; }
.breadcrumb { margin-bottom: 16px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb .separator { margin: 0 8px; }
.tool-header { margin-bottom: 24px; }
.tool-header h1 { font-size: 28px; margin-bottom: 8px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 15px; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; margin-bottom: 24px; box-shadow: var(--shadow-sm); }
.info-card h2 { font-size: 20px; margin-bottom: 12px; color: var(--text-primary); }
.info-card h3 { font-size: 16px; margin: 16px 0 8px; color: var(--text-primary); }
.info-card p { font-size: 14px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 12px; }
.info-card ul { padding-left: 20px; margin-bottom: 12px; }
.info-card li { font-size: 14px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 4px; }

.password-display { display: flex; align-items: center; gap: 12px; padding: 16px 20px; background: var(--bg-tertiary); border-radius: 12px; margin-bottom: 16px; }
.password-text { flex: 1; font-family: 'SF Mono', Monaco, Consolas, monospace; font-size: 18px; color: var(--text-primary); word-break: break-all; }
.btn-copy, .btn-refresh { background: none; border: none; cursor: pointer; font-size: 20px; padding: 4px 8px; border-radius: 6px; transition: background 0.2s; }
.btn-copy:hover, .btn-refresh:hover { background: var(--border-color); }

.password-strength { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
.strength-bar { flex: 1; height: 6px; background: var(--border-color); border-radius: 3px; overflow: hidden; }
.strength-fill { height: 100%; border-radius: 3px; transition: all 0.3s; }
.strength-label { font-size: 13px; font-weight: 600; min-width: 40px; }

.settings { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; }
.setting-row { display: flex; align-items: center; gap: 12px; }
.setting-row > label:first-child { font-size: 14px; font-weight: 600; color: var(--text-primary); min-width: 80px; }
.range-control { display: flex; align-items: center; gap: 12px; flex: 1; }
.range-control input[type="range"] { flex: 1; accent-color: var(--accent-color); }
.range-number { width: 60px; padding: 4px 8px; border: 1px solid var(--border-color); border-radius: 6px; text-align: center; background: var(--bg-input); color: var(--text-primary); font-size: 14px; }
.checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 14px; color: var(--text-secondary); cursor: pointer; }
.checkbox-label input { accent-color: var(--accent-color); }

.batch-section { border-top: 1px solid var(--border-color); padding-top: 20px; }
.batch-section h3 { font-size: 16px; margin-bottom: 12px; color: var(--text-primary); }
.batch-controls { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.batch-controls label { font-size: 14px; color: var(--text-secondary); }
.batch-input { width: 60px; padding: 6px 8px; border: 1px solid var(--border-color); border-radius: 6px; text-align: center; background: var(--bg-input); color: var(--text-primary); }
.batch-output { width: 100%; padding: 12px; border: 1px solid var(--border-color); border-radius: 8px; background: var(--bg-input); color: var(--text-primary); font-family: 'SF Mono', monospace; font-size: 13px; resize: vertical; box-sizing: border-box; }

.btn { padding: 8px 18px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; transition: all 0.2s; }
.btn-primary { background: var(--accent-color); color: white; }
.btn-primary:hover { opacity: 0.9; }
.action-bar { margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--border-color); }
.status-msg { font-size: 13px; color: var(--accent-color); }
</style>
